// src/pages/Dashboard.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useClientes }     from "../hooks/useClientes";
import { useCotizaciones } from "../hooks/useCotizaciones";
import { usePedidos }      from "../hooks/usePedidos";
import { useRecompra }     from "../hooks/useRecompra";
import { useReuniones }    from "../hooks/useReuniones";
import { format, parseISO, startOfMonth } from "date-fns";
import { es as esLocale } from "date-fns/locale";

const ESTADOS = ["Pendiente", "Aprobada", "Rechazada", "Vencida", "Ganada", "Perdida"];

const ESTADO_STYLE = {
  "Pendiente":  { bg: "#E6F1FB", color: "#185FA5" },
  "Aprobada":   { bg: "#EAF3DE", color: "#3B6D11" },
  "Rechazada":  { bg: "#FCEBEB", color: "#A32D2D" },
  "Vencida":    { bg: "#FAEEDA", color: "#854F0B" },
  "Ganada":     { bg: "#E1F5EE", color: "#0F6E56" },
  "Perdida":    { bg: "#FCEBEB", color: "#A32D2D" },
};

const ESTADO_COLOR = {
  "Pendiente":  "#3B82F6",
  "Aprobada":   "#10B981",
  "Rechazada":  "#DC2626",
  "Vencida":    "#F59E0B",
  "Ganada":     "#1D9E75",
  "Perdida":    "#DC2626",
};

// ── Animation styles ──────────────────────────────────────────────────────────
function DashStyles() {
  return (
    <style>{`
      @keyframes _dFadeUp {
        from { opacity: 0; transform: translateY(10px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes _dGrowY {
        from { transform: scaleY(0); }
        to   { transform: scaleY(1); }
      }
      .d-kpi {
        animation: _dFadeUp 320ms cubic-bezier(0.23,1,0.32,1) both;
      }
      .d-card {
        transition: box-shadow 220ms cubic-bezier(0.23,1,0.32,1),
                    transform  220ms cubic-bezier(0.23,1,0.32,1);
      }
      .d-card:hover {
        box-shadow: 0 8px 32px rgba(58,60,122,0.10);
        transform: translateY(-2px);
      }
      .d-btn { transition: transform 160ms ease-out; display: inline-block; }
      .d-btn:active { transform: scale(0.97); }
      .d-item { animation: _dFadeUp 260ms cubic-bezier(0.23,1,0.32,1) both; }
      .d-bar {
        transform-box: fill-box;
        transform-origin: bottom;
        animation: _dGrowY 500ms cubic-bezier(0.23,1,0.32,1) both;
      }
    `}</style>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function KPI({ label, valor, sub, subColor, borderColor, icon, delay = 0 }) {
  return (
    <div
      className="d-kpi d-card"
      style={{
        animationDelay: `${delay}ms`,
        background: "#fff",
        border: "0.5px solid #e8ecf3",
        borderTop: `3px solid ${borderColor || "#3A3C7A"}`,
        borderRadius: 12,
        padding: "16px 18px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500, letterSpacing: "0.02em" }}>{label}</div>
        {icon && (
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: `${borderColor || "#3A3C7A"}18`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, flexShrink: 0,
          }}>
            {icon}
          </div>
        )}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.5px" }}>{valor}</div>
      {sub && (
        <div style={{ fontSize: 10, marginTop: 5, color: subColor || "#64748b", fontWeight: 500 }}>{sub}</div>
      )}
    </div>
  );
}

// ── SVG bar chart ─────────────────────────────────────────────────────────────
function BarChartMensual({ data }) {
  const max = Math.max(...data.map(d => d.ingreso), 1);
  const W = 520, H = 100, PAD = 20;
  const barW = (W - PAD * 2) / data.length;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 22}`} style={{ overflow: "visible" }}>
      {data.map((d, i) => {
        const barH = Math.max((d.ingreso / max) * H, d.ingreso > 0 ? 4 : 0);
        const x    = PAD + i * barW;
        const y    = H - barH;
        const isCurrent = i === data.length - 1;
        return (
          <g key={i}>
            <rect
              className="d-bar"
              x={x + barW * 0.12} y={y}
              width={barW * 0.76} height={barH}
              fill={isCurrent ? "#3A3C7A" : "#93C5FD"} rx={4}
              style={{ animationDelay: `${i * 60 + 150}ms` }}
            />
            {d.ingreso > 0 && (
              <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize={8} fill="#475569" fontWeight="600">
                {d.ingreso >= 1000 ? `${(d.ingreso / 1000).toFixed(0)}k` : d.ingreso}
              </text>
            )}
            <text x={x + barW / 2} y={H + 15} textAnchor="middle" fontSize={9} fill="#94a3b8">
              {d.mes}
            </text>
          </g>
        );
      })}
      <line x1={PAD} y1={H} x2={W - PAD} y2={H} stroke="#f1f5f9" strokeWidth={1} />
    </svg>
  );
}

// ── Funnel bar ────────────────────────────────────────────────────────────────
function FunnelBar({ label, count, valor, maxCount, color, delay = 0, mounted }) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
        <span style={{ color: "#475569", fontWeight: 500 }}>{label}</span>
        <span style={{ color: "#1e293b", fontWeight: 700 }}>
          {count}
          <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 10 }}> · S/ {(valor / 1000).toFixed(1)}k</span>
        </span>
      </div>
      <div style={{ background: "#f1f5f9", borderRadius: 6, height: 8, overflow: "hidden" }}>
        <div style={{
          background: color,
          height: 8,
          borderRadius: 6,
          width: mounted ? `${pct}%` : "0%",
          transition: `width 600ms cubic-bezier(0.23,1,0.32,1) ${delay}ms`,
        }} />
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { clientes, loading: loadingClientes } = useClientes();
  const { cotizaciones, loading: loadingCot, kpis } = useCotizaciones();
  const { pedidos }    = usePedidos();
  const { reuniones }  = useReuniones();
  const recompra       = useRecompra(clientes, pedidos, reuniones);
  const [metaMensual, setMetaMensual] = useState(() =>
    parseInt(localStorage.getItem("crm_meta_mensual") || "50000", 10)
  );
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaInput, setMetaInput]     = useState("");
  const [mounted, setMounted]         = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  function parsearLocal(str) {
    if (!str) return null;
    const [y, m, d] = String(str).split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const hoyD = new Date(); hoyD.setHours(0, 0, 0, 0);

  const clientesVencidos = clientes
    .filter(c => {
      if (["Cerrado", "Perdido"].includes(c.estado) || !c.proximoSeguimiento) return false;
      return parsearLocal(c.proximoSeguimiento) < hoyD;
    })
    .sort((a, b) => (a.proximoSeguimiento || "").localeCompare(b.proximoSeguimiento || ""))
    .slice(0, 5);

  const vencidos = cotizaciones
    .filter(c => {
      if (["Ganada", "Cerrada", "Aprobada", "Perdida", "Rechazada", "Vencida"].includes(c.estado) || !c.proximoSeguimiento) return false;
      return parsearLocal(c.proximoSeguimiento) < hoyD;
    })
    .sort((a, b) => (a.proximoSeguimiento || "").localeCompare(b.proximoSeguimiento || ""))
    .slice(0, 5);

  const en3D = new Date(hoyD); en3D.setDate(en3D.getDate() + 3);
  const proximos = cotizaciones
    .filter(c => {
      if (!c.proximoSeguimiento || ["Ganada", "Cerrada", "Aprobada", "Perdida", "Rechazada", "Vencida"].includes(c.estado)) return false;
      const f = parsearLocal(c.proximoSeguimiento);
      return f >= hoyD && f <= en3D;
    })
    .sort((a, b) => (a.proximoSeguimiento || "").localeCompare(b.proximoSeguimiento || ""))
    .slice(0, 5);

  const ultimasCot = cotizaciones.slice(0, 6);

  const funnelData = useMemo(() => {
    const maxCount = Math.max(...ESTADOS.map(e => cotizaciones.filter(c => c.estado === e).length), 1);
    return ESTADOS.map(estado => ({
      label: estado,
      count: cotizaciones.filter(c => c.estado === estado).length,
      valor: cotizaciones.filter(c => c.estado === estado).reduce((s, c) => s + (Number(c.valor) || 0), 0),
      color: ESTADO_COLOR[estado],
      maxCount,
    }));
  }, [cotizaciones]);

  const ingresoPorMes = useMemo(() => {
    const hoy = new Date();
    const meses = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
      const key = format(d, "MMM", { locale: esLocale });
      meses[key] = { mes: key, ingreso: 0 };
    }
    cotizaciones.forEach(c => {
      if (c.estado !== "Ganada") return;
      let fecha = null;
      if (c.creadoEn?.toDate) fecha = c.creadoEn.toDate();
      else if (c.creadoEn) fecha = new Date(c.creadoEn);
      if (!fecha) return;
      const key = format(fecha, "MMM", { locale: esLocale });
      if (meses[key]) meses[key].ingreso += Number(c.valor) || 0;
    });
    return Object.values(meses);
  }, [cotizaciones]);

  const topClientes = useMemo(() => {
    const map = {};
    cotizaciones.forEach(c => {
      if (!c.clienteNombre) return;
      if (!map[c.clienteNombre]) {
        map[c.clienteNombre] = { nombre: c.clienteNombre, empresa: c.clienteEmpresa || "", total: 0, cots: 0 };
      }
      map[c.clienteNombre].cots++;
      if (c.estado === "Ganada") map[c.clienteNombre].total += Number(c.valor) || 0;
    });
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 5);
  }, [cotizaciones]);

  const ingresosEsteMes = useMemo(() => {
    const inicio = startOfMonth(new Date());
    return cotizaciones
      .filter(c => {
        if (c.estado !== "Ganada") return false;
        let fecha = c.creadoEn?.toDate ? c.creadoEn.toDate() : c.creadoEn ? new Date(c.creadoEn) : null;
        return fecha && fecha >= inicio;
      })
      .reduce((s, c) => s + (Number(c.valor) || 0), 0);
  }, [cotizaciones]);

  const progresoMeta     = Math.min((ingresosEsteMes / metaMensual) * 100, 100);
  const ticketPromedio   = kpis.cerradas > 0 ? Math.round(kpis.valorCerrado / kpis.cerradas) : 0;
  const valorNegociacion = cotizaciones
    .filter(c => c.estado === "Aprobada")
    .reduce((s, c) => s + (Number(c.valor) || 0), 0);

  if (loadingClientes || loadingCot) return (
    <div style={{ textAlign: "center", padding: 60, color: "#94a3b8", fontSize: 14 }}>
      Cargando dashboard...
    </div>
  );

  const kpiList = [
    { label: "Clientes",             valor: clientes.length,                                    sub: "registrados en CRM",                     borderColor: "#3A3C7A", icon: "👥" },
    { label: "Cotizaciones activas", valor: kpis.activas,                                       sub: `S/ ${kpis.pipeline.toLocaleString("es-PE")} pipeline`, subColor: "#D97706", borderColor: "#D97706", icon: "📋" },
    { label: "Ingresos confirmados", valor: `S/ ${kpis.valorCerrado.toLocaleString("es-PE")}`,  sub: `${kpis.cerradas} ganadas · ${kpis.conversion}% conversión`, subColor: "#1D9E75", borderColor: "#1D9E75", icon: "💰" },
    { label: "Ticket promedio",      valor: `S/ ${ticketPromedio.toLocaleString("es-PE")}`,     sub: "por cotización ganada",                   borderColor: "#8B5CF6", icon: "🎯" },
    { label: "Valor perdido",        valor: `S/ ${kpis.valorPerdido.toLocaleString("es-PE")}`,  sub: `${kpis.perdidas} cotizaciones perdidas`,  subColor: "#DC2626",   borderColor: "#DC2626", icon: "❌" },
    { label: "Seguim. vencidos",     valor: kpis.vencidas,                                      sub: kpis.vencidas > 0 ? "requieren atención urgente" : "todo al día", subColor: kpis.vencidas > 0 ? "#DC2626" : "#1D9E75", borderColor: kpis.vencidas > 0 ? "#DC2626" : "#1D9E75", icon: "⏰" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <DashStyles />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.3px" }}>Dashboard</h1>
          <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 2, textTransform: "capitalize" }}>
            {format(new Date(), "EEEE dd 'de' MMMM, yyyy", { locale: esLocale })}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link to="/clientes/nuevo" className="d-btn" style={{
            background: "#fff", color: "#3A3C7A", textDecoration: "none",
            border: "1.5px solid #3A3C7A", fontSize: 13, fontWeight: 600,
            padding: "8px 14px", borderRadius: 8,
          }}>
            + Nuevo cliente
          </Link>
          <Link to="/cotizaciones" className="d-btn" style={{
            background: "#F97316", color: "#000", textDecoration: "none",
            fontSize: 13, fontWeight: 700, padding: "8px 18px", borderRadius: 8,
          }}>
            + Nueva cotización
          </Link>
        </div>
      </div>

      {/* KPIs — 3 por fila × 2 filas */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {kpiList.map((kpi, i) => (
          <KPI key={kpi.label} {...kpi} delay={i * 60} />
        ))}
      </div>

      {/* Pipeline + Meta */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>

        <div className="d-card" style={{ background: "#fff", border: "0.5px solid #e8ecf3", borderRadius: 12, padding: 18 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 16 }}>Pipeline por estado</h3>
          {funnelData.map((f, i) => (
            <FunnelBar key={f.label} {...f} delay={i * 80} mounted={mounted} />
          ))}
        </div>

        <div className="d-card" style={{ background: "#fff", border: "0.5px solid #e8ecf3", borderRadius: 12, padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Meta mensual</h3>
            <button
              className="d-btn"
              onClick={() => { setEditingMeta(true); setMetaInput(String(metaMensual)); }}
              style={{ fontSize: 10, color: "#3A3C7A", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
            >
              Cambiar meta
            </button>
          </div>

          {editingMeta && (
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              <input
                type="number" value={metaInput}
                onChange={e => setMetaInput(e.target.value)}
                style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 8px", fontSize: 13 }}
                placeholder="Meta en S/"
              />
              <button
                className="d-btn"
                onClick={() => {
                  const n = Number(metaInput) || metaMensual;
                  setMetaMensual(n);
                  localStorage.setItem("crm_meta_mensual", String(n));
                  setEditingMeta(false);
                }}
                style={{ background: "#3A3C7A", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}
              >
                OK
              </button>
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 8 }}>
              <span style={{ color: "#64748b" }}>Ingresos este mes</span>
              <span style={{ color: "#1e293b", fontWeight: 700 }}>
                S/ {ingresosEsteMes.toLocaleString("es-PE")}
              </span>
            </div>
            <div style={{ background: "#f1f5f9", borderRadius: 8, height: 14, overflow: "hidden" }}>
              <div style={{
                background: progresoMeta >= 100 ? "#1D9E75" : progresoMeta >= 60 ? "#F97316" : "#3A3C7A",
                height: 14,
                width: mounted ? `${progresoMeta}%` : "0%",
                borderRadius: 8,
                transition: "width 800ms cubic-bezier(0.23,1,0.32,1) 400ms",
              }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginTop: 6 }}>
              <span style={{ color: progresoMeta >= 100 ? "#1D9E75" : "#64748b", fontWeight: 600 }}>
                {progresoMeta.toFixed(0)}% completado
              </span>
              <span style={{ color: "#94a3b8" }}>
                Meta: S/ {metaMensual.toLocaleString("es-PE")}
              </span>
            </div>
          </div>

          <div style={{ background: "#f8fafc", borderRadius: 10, padding: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <div style={{ fontSize: 9, color: "#94a3b8", marginBottom: 3, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>Faltan para meta</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: ingresosEsteMes >= metaMensual ? "#1D9E75" : "#1e293b" }}>
                {ingresosEsteMes >= metaMensual
                  ? "✓ Meta cumplida"
                  : `S/ ${Math.max(0, metaMensual - ingresosEsteMes).toLocaleString("es-PE")}`}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 9, color: "#94a3b8", marginBottom: 3, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>En negociación</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#3B6D11" }}>
                S/ {valorNegociacion.toLocaleString("es-PE")}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico ingresos */}
      <div className="d-card" style={{ background: "#fff", border: "0.5px solid #e8ecf3", borderRadius: 12, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
            Ingresos por mes — últimos 6 meses
          </h3>
          <div style={{ display: "flex", gap: 12, fontSize: 10, color: "#94a3b8" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 10, height: 10, background: "#93C5FD", borderRadius: 2, display: "inline-block" }} />
              Meses anteriores
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 10, height: 10, background: "#3A3C7A", borderRadius: 2, display: "inline-block" }} />
              Mes actual
            </span>
          </div>
        </div>
        <BarChartMensual data={ingresoPorMes} />
        {ingresoPorMes.every(m => m.ingreso === 0) && (
          <p style={{ textAlign: "center", color: "#94a3b8", fontSize: 12, marginTop: 8 }}>
            Sin cotizaciones cerradas registradas aún.
          </p>
        )}
      </div>

      {/* Vencidos cotizaciones */}
      {vencidos.length > 0 && (
        <div style={{ background: "#FFF5F5", border: "1px solid #FECACA", borderRadius: 12, padding: 16 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: "#DC2626", marginBottom: 12 }}>
            🔴 Seguimientos vencidos ({vencidos.length})
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {vencidos.map((c, i) => (
              <div key={c.id} className="d-item" style={{
                animationDelay: `${i * 50}ms`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "#fff", borderRadius: 8, padding: "8px 12px", border: "0.5px solid #FECACA",
              }}>
                <div>
                  <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{c.clienteNombre}</span>
                  <span style={{ color: "#94a3b8", fontSize: 11, marginLeft: 8 }}>{c.clienteEmpresa}</span>
                  {c.numeroCot && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#3A3C7A", background: "#EEF2FF", padding: "1px 6px", borderRadius: 4, marginLeft: 6 }}>
                      {c.numeroCot}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "#DC2626", fontWeight: 600 }}>
                    {format(parseISO(c.proximoSeguimiento), "dd/MM/yyyy")}
                  </span>
                  <Link to="/seguimiento" className="d-btn" style={{
                    fontSize: 11, background: "#FECACA", color: "#DC2626",
                    padding: "3px 8px", borderRadius: 5, textDecoration: "none", fontWeight: 600,
                  }}>
                    Ver
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clientes vencidos */}
      {clientesVencidos.length > 0 && (
        <div style={{ background: "#FFF5F5", border: "1px solid #FECACA", borderRadius: 12, padding: 16 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: "#DC2626", marginBottom: 12 }}>
            🔴 Clientes sin contactar vencidos ({clientesVencidos.length})
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {clientesVencidos.map((c, i) => (
              <div key={c.id} className="d-item" style={{
                animationDelay: `${i * 50}ms`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "#fff", borderRadius: 8, padding: "8px 12px", border: "0.5px solid #FECACA",
              }}>
                <div>
                  <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{c.nombre}</span>
                  {c.empresa && <span style={{ color: "#94a3b8", fontSize: 11, marginLeft: 8 }}>{c.empresa}</span>}
                  {c.responsable && <span style={{ color: "#94a3b8", fontSize: 10, marginLeft: 8 }}>· {c.responsable}</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "#DC2626", fontWeight: 600 }}>
                    {c.proximoSeguimiento?.split("-").reverse().join("/")}
                  </span>
                  <Link to="/seguimiento" className="d-btn" style={{
                    fontSize: 11, background: "#FECACA", color: "#DC2626",
                    padding: "3px 8px", borderRadius: 5, textDecoration: "none", fontWeight: 600,
                  }}>
                    Ver
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Próximos + Top clientes */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>

        <div className="d-card" style={{ background: "#fff", border: "0.5px solid #e8ecf3", borderRadius: 12, padding: 18 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 14 }}>
            Próximos seguimientos (3 días)
          </h3>
          {proximos.length === 0 ? (
            <p style={{ color: "#94a3b8", fontSize: 13 }}>No hay seguimientos próximos.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {proximos.map((c, i) => {
                const estilo = ESTADO_STYLE[c.estado] || ESTADO_STYLE["Pendiente"];
                return (
                  <div key={c.id} className="d-item" style={{
                    animationDelay: `${i * 50}ms`,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: "#f8fafc", borderRadius: 8, padding: "8px 12px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: "#E6F1FB", color: "#185FA5",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, fontWeight: 800, flexShrink: 0,
                      }}>
                        {(c.clienteNombre || "?").charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {c.clienteNombre}
                          {c.numeroCot && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#3A3C7A", background: "#EEF2FF", padding: "1px 6px", borderRadius: 4, marginLeft: 6 }}>
                              {c.numeroCot}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>
                          {c.clienteEmpresa}{c.proyecto ? ` · ${c.proyecto}` : ""}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 8, background: estilo.bg, color: estilo.color }}>
                        {c.estado}
                      </span>
                      <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
                        {c.proximoSeguimiento ? format(parseISO(c.proximoSeguimiento), "dd/MM") : ""}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="d-card" style={{ background: "#fff", border: "0.5px solid #e8ecf3", borderRadius: 12, padding: 18 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 14 }}>
            Top clientes por ingresos
          </h3>
          {topClientes.length === 0 || topClientes.every(c => c.total === 0) ? (
            <p style={{ color: "#94a3b8", fontSize: 13 }}>Sin cotizaciones cerradas aún.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {topClientes.map((c, i) => {
                const maxVal = topClientes[0].total || 1;
                const pct = (c.total / maxVal) * 100;
                const medalColor = i === 0 ? "#F59E0B" : i === 1 ? "#94A3B8" : i === 2 ? "#B45309" : "#CBD5E1";
                return (
                  <div key={c.nombre} className="d-item" style={{ animationDelay: `${i * 60}ms` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: medalColor, width: 18 }}>#{i + 1}</span>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{c.nombre}</div>
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>{c.empresa} · {c.cots} cot.</div>
                        </div>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#1e293b" }}>
                        S/ {c.total.toLocaleString("es-PE")}
                      </span>
                    </div>
                    <div style={{ background: "#f1f5f9", borderRadius: 4, height: 5, overflow: "hidden" }}>
                      <div style={{
                        background: medalColor, height: 5, borderRadius: 4,
                        width: mounted ? `${pct}%` : "0%",
                        transition: `width 600ms cubic-bezier(0.23,1,0.32,1) ${i * 80}ms`,
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Últimas cotizaciones */}
      <div className="d-card" style={{ background: "#fff", border: "0.5px solid #e8ecf3", borderRadius: 12, padding: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Últimas cotizaciones</h3>
          <Link to="/cotizaciones" style={{ fontSize: 11, color: "#3A3C7A", textDecoration: "none", fontWeight: 700 }}>
            Ver todas →
          </Link>
        </div>
        {ultimasCot.length === 0 ? (
          <p style={{ color: "#94a3b8", fontSize: 13 }}>No hay cotizaciones aún.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {ultimasCot.map((c, i) => {
              const estilo = ESTADO_STYLE[c.estado] || ESTADO_STYLE["Pendiente"];
              return (
                <div key={c.id} className="d-item" style={{
                  animationDelay: `${i * 40}ms`,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 4px", borderBottom: "0.5px solid #f1f5f9",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: "50%",
                      background: "#f1f5f9", color: "#64748b",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 800, flexShrink: 0,
                    }}>
                      {(c.clienteNombre || "?").charAt(0)}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                        {c.clienteNombre}
                        {c.numeroCot && (
                          <span style={{ fontSize: 9, fontWeight: 700, color: "#3A3C7A", background: "#EEF2FF", padding: "1px 5px", borderRadius: 4, marginLeft: 5 }}>
                            {c.numeroCot}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: "#94a3b8" }}>
                        {c.clienteEmpresa}{c.proyecto ? ` · ${c.proyecto}` : ""}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 8, background: estilo.bg, color: estilo.color }}>
                      {c.estado}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#1e293b" }}>
                      S/ {Number(c.valor || 0).toLocaleString("es-PE")}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Recompra inteligente ── */}
      {recompra.length > 0 && (() => {
        const inactivos = recompra.filter(r => r.nivelRiesgo === "inactivo");
        const frios     = recompra.filter(r => r.nivelRiesgo === "frío");
        const calientes = recompra.filter(r => r.nivelRiesgo === "caliente" || r.nivelRiesgo === "tibio");
        const mostrar   = recompra.filter(r => !r.contactadoReciente).slice(0, 6);
        if (mostrar.length === 0) return null;
        const RIESGO_ST = {
          inactivo: { bg:"#FCEBEB", color:"#A32D2D", dot:"#DC2626" },
          "frío":   { bg:"#EFF6FF", color:"#1D4ED8", dot:"#3B82F6" },
          tibio:    { bg:"#FAEEDA", color:"#854F0B", dot:"#D97706" },
          caliente: { bg:"#E1F5EE", color:"#0F6E56", dot:"#1D9E75" },
        };
        return (
          <div style={{ background:"#fff", border:"0.5px solid #e2e8f0", borderRadius:14, padding:"16px 18px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:"#1e293b" }}>🔄 Recompra inteligente</div>
                <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>
                  <span style={{ color:"#DC2626", fontWeight:600 }}>{inactivos.length} inactivos</span>
                  {" · "}
                  <span style={{ color:"#3B82F6", fontWeight:600 }}>{frios.length} fríos</span>
                  {" · "}
                  <span style={{ color:"#1D9E75", fontWeight:600 }}>{calientes.length} calientes</span>
                </div>
              </div>
              <Link to="/clientes" style={{ fontSize:11, fontWeight:600, padding:"5px 12px", borderRadius:8, background:"#f1f5f9", color:"#475569", textDecoration:"none" }}>
                Ver clientes →
              </Link>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {mostrar.map(r => {
                const st = RIESGO_ST[r.nivelRiesgo] || RIESGO_ST.tibio;
                return (
                  <div key={r.clienteId} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", background:"#f8fafc", borderRadius:8, border:"0.5px solid #e2e8f0" }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:st.dot, flexShrink:0 }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <Link to={`/clientes/${r.clienteId}/perfil`} style={{ fontSize:12, fontWeight:600, color:"#1e293b", textDecoration:"none" }}>
                        {r.clienteNombre}
                      </Link>
                      {r.clienteEmpresa && <span style={{ fontSize:10, color:"#94a3b8", marginLeft:6 }}>{r.clienteEmpresa}</span>}
                    </div>
                    <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:8, background:st.bg, color:st.color, flexShrink:0 }}>
                      {r.diasDesdeUltima}d sin comprar
                    </span>
                    {r.frecuenciaPromedio && (
                      <span style={{ fontSize:10, color:"#94a3b8", flexShrink:0 }}>frec. {r.frecuenciaPromedio}d</span>
                    )}
                    {r.telefono && (
                      <button
                        onClick={() => window.open(`https://wa.me/51${r.telefono.replace(/\D/g,"")}?text=Hola ${r.clienteNombre.split(" ")[0]}, te contacto para saber cómo va tu proyecto 🏗`, "_blank")}
                        style={{ fontSize:10, fontWeight:600, padding:"4px 10px", borderRadius:6, background:"#DCF8C6", color:"#128C7E", border:"none", cursor:"pointer", flexShrink:0 }}
                      >
                        WhatsApp
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

    </div>
  );
}
